import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useInventoryItem } from '../../hooks/useInventory';
import { inventoryService } from '../../services/inventoryService';
import { useAuth } from '../../context/AuthContext';
import Button from '../../components/ui/Button';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Card from '../../components/ui/Card';
import { Pencil, Package, TrendUp, CurrencyDollar } from 'phosphor-react';

const InventoryDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading, businessId } = useAuth();
  const { item, loading: itemLoading, error: itemError } = useInventoryItem(id || null);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);

  // Load transactions when item is loaded
  React.useEffect(() => {
    if (item && id) {
      loadTransactions();
    }
  }, [item, id]);

  const loadTransactions = async () => {
    if (!id) return;

    try {
      setTransactionsLoading(true);
      const { transactions: data, error } = await inventoryService.getInventoryTransactions(id);
      if (error) {
        console.error('Error loading transactions:', error);
      } else {
        setTransactions(data);
      }
    } catch (err) {
      console.error('Error loading transactions:', err);
    } finally {
      setTransactionsLoading(false);
    }
  };

  // Show loading while fetching item data or auth/business is loading
  if ((itemLoading && id) || authLoading || (isAuthenticated && !businessId)) {
    return (
      <div className="flex flex-col justify-center items-center h-64 space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
        <p className="text-gray-600">
          {(itemLoading && id) ? 'Loading item details...' :
           authLoading ? 'Loading authentication...' :
           'Loading business data...'}
        </p>
      </div>
    );
  }

  // Show message if user is authenticated but has no business
  if (isAuthenticated && !businessId) {
    return (
      <div className="flex flex-col justify-center items-center h-64 space-y-4">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Business Profile Required</h2>
          <p className="text-gray-600 mb-4">
            You need to create a business profile before you can view inventory details.
          </p>
          <p className="text-sm text-gray-500">
            Please complete your business setup first.
          </p>
        </div>
      </div>
    );
  }

  // Show login prompt if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="flex flex-col justify-center items-center h-64 space-y-4">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Authentication Required</h2>
          <p className="text-gray-600">
            Please log in to access inventory details.
          </p>
        </div>
      </div>
    );
  }

  if (itemError || !item) {
    return (
      <div className="flex flex-col justify-center items-center h-64 space-y-4">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Item Not Found</h2>
          <p className="text-gray-600 mb-4">
            The inventory item you're looking for doesn't exist or you don't have permission to view it.
          </p>
          <Button onClick={() => navigate('/inventory')}>
            Back to Inventory
          </Button>
        </div>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: 'Inventory', href: '/inventory' },
    { label: item.name, current: true }
  ];

  const getTransactionTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      purchase: 'Purchase',
      repair_usage: 'Repair Usage',
      sale: 'Sale',
      return: 'Return'
    };
    return labels[type] || type;
  };

  const getTransactionTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      purchase: 'text-green-600 bg-green-100',
      repair_usage: 'text-orange-600 bg-orange-100',
      sale: 'text-blue-600 bg-blue-100',
      return: 'text-purple-600 bg-purple-100'
    };
    return colors[type] || 'text-gray-600 bg-gray-100';
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Breadcrumbs */}
      <Breadcrumb items={breadcrumbItems} />

      {/* Header */}
      <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{item.name}</h1>
          {item.sku && (
            <p className="text-lg text-gray-600">SKU: {item.sku}</p>
          )}
        </div>
        <Button
          variant="primary"
          onClick={() => navigate(`/inventory/${item.id}/edit`)}
          className="flex items-center"
        >
          <Pencil className="w-4 h-4 mr-2" />
          Edit Item
        </Button>
      </div>

      {/* Item Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card>
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Package className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Current Quantity</dt>
                  <dd className="text-lg font-medium text-gray-900">{item.quantity}</dd>
                </dl>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TrendUp className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Average Cost</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {inventoryService.getFormattedCost(item.average_cost)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CurrencyDollar className="h-8 w-8 text-purple-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Sale Price</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {inventoryService.getFormattedCost(item.sale_price)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CurrencyDollar className="h-8 w-8 text-indigo-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Value</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {inventoryService.getFormattedCost(item.average_cost * item.quantity)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Item Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <Card>
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Item Information</h3>
            <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
              <div>
                <dt className="text-sm font-medium text-gray-500">Category</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  <span className="badge badge-info">
                    {inventoryService.getCategoryLabel(item.category)}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Reorder Point</dt>
                <dd className="mt-1 text-sm text-gray-900">{item.reorder_point}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Latest Purchase Cost</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {inventoryService.getFormattedCost(item.purchase_cost)}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Status</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  <span className={`badge ${item.is_active ? 'badge-success' : 'badge-danger'}`}>
                    {item.is_active ? 'Active' : 'Inactive'}
                  </span>
                </dd>
              </div>
            </dl>
            {item.description && (
              <div className="mt-6">
                <dt className="text-sm font-medium text-gray-500">Description</dt>
                <dd className="mt-1 text-sm text-gray-900">{item.description}</dd>
              </div>
            )}
          </div>
        </Card>

        <Card>
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Stock Status</h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Current Stock</span>
                  <span className="font-medium">{item.quantity}</span>
                </div>
                <div className="mt-1">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${
                        item.quantity <= item.reorder_point ? 'bg-red-500' :
                        item.quantity <= item.reorder_point * 1.5 ? 'bg-yellow-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${Math.min((item.quantity / (item.reorder_point * 2)) * 100, 100)}%` }}
                    ></div>
                  </div>
                </div>
              </div>
              <div className="pt-4 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Reorder Point</span>
                  <span className="text-sm font-medium">{item.reorder_point}</span>
                </div>
                {item.quantity <= item.reorder_point && (
                  <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-800">
                      ⚠️ Low stock alert! Current quantity is below reorder point.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Transactions Table */}
      <Card>
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Transaction History</h3>

          {transactionsLoading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-500"></div>
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-8">
              <Package className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No transactions</h3>
              <p className="mt-1 text-sm text-gray-500">
                No inventory transactions have been recorded for this item yet.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quantity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Unit Cost
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Reference
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {transactions.map((transaction) => (
                    <tr key={transaction.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(transaction.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTransactionTypeColor(transaction.type)}`}>
                          {getTransactionTypeLabel(transaction.type)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className={transaction.quantity < 0 ? 'text-red-600' : 'text-green-600'}>
                          {transaction.quantity > 0 ? '+' : ''}{transaction.quantity}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {inventoryService.getFormattedCost(transaction.unit_cost)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {transaction.reference_type && transaction.reference_id ? (
                          <button
                            onClick={() => {
                              // Navigate to the related transaction based on type
                              switch (transaction.reference_type) {
                                case 'purchase_order':
                                  navigate(`/purchase-orders/${transaction.reference_id}`);
                                  break;
                                case 'repair_order':
                                  navigate(`/repair-orders/${transaction.reference_id}`);
                                  break;
                                case 'invoice':
                                  navigate(`/invoices/${transaction.reference_id}`);
                                  break;
                                default:
                                  console.log('Unknown reference type:', transaction.reference_type);
                              }
                            }}
                            className="text-primary-600 hover:text-primary-900 underline"
                          >
                            {transaction.reference_type === 'purchase_order' ? (transaction.reference_number || transaction.reference_id) : `View ${transaction.reference_type.replace('_', ' ')}`}
                          </button>
                        ) : (
                          '-'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default InventoryDetails;